/**
 * When Squares accepts plays: real toss, or within N minutes of scheduled start
 * (or already in-play / overs under way).
 */

/** Must match backend `SQUARES_PLAY_OPENS_MINUTES_BEFORE_START`. */
export const SQUARES_PLAY_OPENS_MINUTES_BEFORE_START = 30;

const TOSS_WON_IN_COMMENTARY = /\b(won|wins|winning|win)\s+the\s+toss\b/i;
const TOSS_WON_RE = /^(.+?)\s+won the toss\b/i;

function normalizeTeamName(s) {
  return String(s ?? "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, " ");
}

/** Decimal often ships `"0 won the toss…"` before the real toss. */
export function isPlaceholderTossWinnerTeam(name) {
  const n = normalizeTeamName(name);
  if (!n) return true;
  if (n === "0" || n === "-" || n === "n/a" || n === "na" || n === "unknown" || n === "none" || n === "null") {
    return true;
  }
  return /^\d+$/.test(n);
}

export function parseTossWinnerTeamFromText(text) {
  const raw = String(text ?? "").trim();
  if (!raw) return null;
  const m = raw.match(TOSS_WON_RE);
  const team = m ? m[1].trim() : null;
  if (!team || isPlaceholderTossWinnerTeam(team)) return null;
  return team;
}

/**
 * @param {unknown} commentary
 * @returns {boolean}
 */
export function commentaryIndicatesRealTossWon(commentary) {
  const com = commentary?.commentaries;
  if (!com || typeof com !== "object" || Array.isArray(com)) return false;
  for (const key of Object.keys(com)) {
    const arr = com[key];
    if (!Array.isArray(arr)) continue;
    for (const item of arr) {
      const text = item?.commentary != null ? String(item.commentary) : "";
      if (!text || !TOSS_WON_IN_COMMENTARY.test(text)) continue;
      if (parseTossWinnerTeamFromText(text)) return true;
      const m = text.match(/^(.+?)\s+(?:won|wins|winning|win)\s+the\s+toss\b/i);
      if (m?.[1] && !isPlaceholderTossWinnerTeam(m[1])) return true;
    }
  }
  return false;
}

/**
 * @param {unknown} toss
 * @returns {boolean}
 */
export function scorecardTossConfirmed(toss) {
  if (toss == null) return false;
  if (typeof toss === "object" && !Array.isArray(toss)) {
    const won = toss.won != null ? String(toss.won).trim() : "";
    return Boolean(won && !isPlaceholderTossWinnerTeam(won));
  }
  const text = String(toss).trim();
  if (!text) return false;
  return parseTossWinnerTeamFromText(text) != null;
}

/**
 * @param {unknown} startDateTime
 * @param {number} [nowMs]
 * @returns {boolean}
 */
export function isWithinSquaresPlayOpenCountdown(startDateTime, nowMs = Date.now()) {
  if (startDateTime == null || startDateTime === "") return false;
  const start = new Date(startDateTime).getTime();
  if (!Number.isFinite(start)) return false;
  const now = Number.isFinite(nowMs) ? nowMs : Date.now();
  return now >= start - SQUARES_PLAY_OPENS_MINUTES_BEFORE_START * 60 * 1000;
}

/**
 * @param {{
 *   commentary?: unknown,
 *   scorecardToss?: unknown,
 *   eventStatusInPlay?: boolean,
 *   oversUnderWay?: boolean,
 *   matchStartDateTime?: unknown,
 *   nowMs?: number,
 * }} input
 * @returns {boolean}
 */
export function squaresPlayOpenWindowFromFeeds(input = {}) {
  const {
    commentary,
    scorecardToss,
    eventStatusInPlay = false,
    oversUnderWay = false,
    matchStartDateTime = null,
    nowMs = Date.now(),
  } = input;

  if (eventStatusInPlay || oversUnderWay) return true;
  if (commentaryIndicatesRealTossWon(commentary) || scorecardTossConfirmed(scorecardToss)) return true;
  return isWithinSquaresPlayOpenCountdown(matchStartDateTime, nowMs);
}
